import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import EmptyState from "@/components/EmptyState";
import Pagination from "@/components/Pagination";
import { ProfitCell, MarginCell } from "@/components/ProfitCell";
import { api, formatApiError } from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";

export default function CostsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { settings } = useAuth();
  const minMargin = settings?.min_margin_percent ?? 20;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [onlyLowMargin] = useState(searchParams.get("only_low_margin") === "true");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const load = async (opts = {}) => {
    setLoading(true);
    try {
      const params = { page: opts.page ?? page, page_size: opts.pageSize ?? pageSize };
      if (q) params.q = q;
      if (onlyLowMargin) params.only_low_margin = true;
      const r = await api.get("/costs/summary", { params });
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, pageSize, onlyLowMargin]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 bg-[#F4F6F8] min-h-full" data-testid="costs-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#171A1F] tracking-tight">İş Maliyetleri</h1>
          <p className="text-xs text-slate-500 mt-1">Gerçekleşen maliyet, brüt kâr ve marj özeti. Minimum marj eşiği: <span className="font-mono font-semibold text-[#FF353A]">%{minMargin}</span></p>
        </div>
      </div>
      <div className="rounded-lg border border-[#DCE2E8] bg-white p-4 shadow-sm">
        <form onSubmit={(e) => { e.preventDefault(); if (page !== 1) setPage(1); else load({ page: 1 }); }} className="flex gap-3 items-center">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="İş no, müşteri veya firma ara..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 border-[#DCE2E8] focus-visible:ring-[#FF353A]" data-testid="costs-search-input" />
          </div>
          <Button type="submit" variant="secondary" className="bg-[#F4F6F8] hover:bg-slate-200 text-[#171A1F] border border-[#DCE2E8]" data-testid="costs-search-apply">Ara</Button>
          <span className="ml-auto text-xs text-slate-500">Toplam <span className="font-semibold text-[#171A1F]">{total.toLocaleString("tr-TR")}</span> kayıt</span>
        </form>
      </div>
      <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-500" data-testid="costs-loading">
            <Loader2 className="animate-spin text-[#FF353A]" size={16} /> Yükleniyor...
          </div>
        ) : items.length === 0 ? (
          <EmptyState title="Kayıt yok" description="Henüz maliyet özeti oluşturulacak iş kaydı yok." />
        ) : (
          <>
            {/* Mobile Card List View */}
            <div className="block md:hidden divide-y divide-[#DCE2E8]">
              {items.map((c) => (
                <div
                  key={c.job_id}
                  onClick={() => navigate(`/isler/${c.job_id}`)}
                  className="p-4 space-y-2 cursor-pointer hover:bg-[#EAF7FF]/20 active:bg-[#EAF7FF]/50 transition-colors"
                  data-testid={`cost-card-${c.job_number}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs font-bold text-[#FF353A]">{c.job_number}</span>
                    <MarginCell value={c.gross_margin} minMargin={minMargin} testId={`gross-margin-mobile-${c.job_number}`} />
                  </div>
                  <div className="text-sm font-semibold text-[#171A1F]">{c.customer_name}</div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 pt-1 border-t border-slate-100">
                    <div>
                      <span className="text-slate-400 block text-[10px]">Satış Tutarı</span>
                      <span className="font-mono font-medium">{formatCurrency(c.sales_amount)}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[10px]">Toplam Maliyet</span>
                      <span className="font-mono font-medium">{formatCurrency(c.total_cost)}</span>
                    </div>
                    <div className="col-span-2 pt-1 flex items-center justify-between">
                      <span className="text-slate-400 text-[10px]">Brüt Kâr:</span>
                      <ProfitCell value={c.gross_profit} testId={`gross-profit-mobile-${c.job_number}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <Table className="hidden md:table">
              <TableHeader>
                <TableRow className="bg-[#F4F6F8] hover:bg-[#F4F6F8]">
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">İş No</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Müşteri</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 text-right">Satış Tutarı</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 text-right">Toplam Maliyet</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 text-right">Brüt Kâr</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 text-right">Brüt Kâr Marjı</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => {
                  return (
                    <TableRow key={c.job_id} className="gp-row cursor-pointer border-b border-[#DCE2E8]" onClick={() => navigate(`/isler/${c.job_id}`)} data-testid={`cost-row-${c.job_number}`}>
                      <TableCell className="font-mono text-sm font-semibold text-[#FF353A]">{c.job_number}</TableCell>
                      <TableCell className="text-sm font-medium text-[#171A1F]">{c.customer_name}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(c.sales_amount)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(c.total_cost)}</TableCell>
                      <TableCell className="text-right"><ProfitCell value={c.gross_profit} testId={`gross-profit-${c.job_number}`} /></TableCell>
                      <TableCell className="text-right"><MarginCell value={c.gross_margin} minMargin={minMargin} testId={`gross-margin-${c.job_number}`} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            />
          </>
        )}
      </div>
    </div>
  );
}
